/**
 * ReservationsStep - Host-specific pre-shift step
 * Shows today's reservations overview for hosts to review
 */

import { Clock, Users, Check, AlertCircle, Star, Phone } from 'lucide-react';
import clsx from 'clsx';

interface Reservation {
  id: string;
  guestName: string;
  time: string;
  partySize: number;
  status: string;
  specialRequests?: string;
  phone?: string;
  isVIP?: boolean;
  allergies?: string[];
}

interface ReservationsStepProps {
  reservations: Reservation[];
  isReviewed: boolean;
  onAcknowledge: () => void;
}

export default function ReservationsStep({
  reservations,
  isReviewed,
  onAcknowledge,
}: ReservationsStepProps) {
  // Group reservations by time slots
  const upcomingReservations = reservations.filter(
    (r) => r.status === 'confirmed' || r.status === 'pending'
  );
  const vipReservations = reservations.filter((r) => r.isVIP);
  const withAllergies = reservations.filter((r) => r.allergies && r.allergies.length > 0);

  return (
    <div>
      <h2 className="text-xl font-bold text-slate-900 mb-1">Today's Reservations</h2>
      <p className="text-slate-500 mb-6">Review the reservation book for your shift</p>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-primary-600">
            {upcomingReservations.length}
          </div>
          <div className="text-xs text-slate-500">Reservations</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-amber-600">
            {vipReservations.length}
          </div>
          <div className="text-xs text-slate-500">VIP Guests</div>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-red-600">
            {withAllergies.length}
          </div>
          <div className="text-xs text-slate-500">Allergies</div>
        </div>
      </div>

      {/* VIP Reservations */}
      {vipReservations.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            VIP Guests
          </h3>
          <div className="space-y-2">
            {vipReservations.map((res) => (
              <div
                key={res.id}
                className="bg-amber-50 border border-amber-200 rounded-xl p-3"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className="font-semibold text-slate-900">{res.guestName}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {res.time} · Party of {res.partySize}
                    </p>
                    {res.specialRequests && (
                      <p className="text-sm text-amber-700 mt-1">{res.specialRequests}</p>
                    )}
                  </div>
                  {res.phone && (
                    <a
                      href={`tel:${res.phone}`}
                      className="p-2 bg-amber-100 rounded-lg text-amber-700"
                    >
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Allergy Alerts */}
      {withAllergies.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            Allergy Alerts
          </h3>
          <div className="space-y-2">
            {withAllergies.map((res) => (
              <div
                key={res.id}
                className="bg-red-50 border border-red-200 rounded-xl p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">{res.guestName}</span>
                  <span className="text-sm text-slate-600">{res.time}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {res.allergies?.map((allergy, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium"
                    >
                      {allergy}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Reservations List */}
      <div className="mb-6">
        <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          All Reservations
        </h3>
        {upcomingReservations.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-auto">
            {upcomingReservations.map((res) => (
              <div
                key={res.id}
                className={clsx(
                  'flex items-center justify-between rounded-lg px-3 py-2',
                  res.status === 'pending' ? 'bg-amber-50' : 'bg-slate-50'
                )}
              >
                <div className="flex items-center gap-3">
                  {res.isVIP && <Star className="w-4 h-4 text-amber-500" />}
                  <div>
                    <p className="font-medium text-slate-900">{res.guestName}</p>
                    {res.status === 'pending' && (
                      <span className="text-xs text-amber-600">Needs confirmation</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-900">{res.time}</p>
                  <p className="text-xs text-slate-500">
                    <Users className="w-3 h-3 inline" /> {res.partySize}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-slate-500">No reservations for today</p>
          </div>
        )}
      </div>

      {/* Acknowledge Button */}
      <button
        onClick={onAcknowledge}
        className={clsx(
          'w-full py-4 rounded-xl font-semibold text-lg transition-all',
          isReviewed
            ? 'bg-emerald-500 text-white'
            : 'bg-primary-600 text-white hover:bg-primary-700'
        )}
      >
        {isReviewed ? (
          <span className="flex items-center justify-center gap-2">
            <Check className="w-5 h-5" />
            Reviewed
          </span>
        ) : (
          "I've Reviewed the Reservations"
        )}
      </button>
    </div>
  );
}
